import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useCreateGoodRequestAction } from '@/services/fulfillment-service';
import { Customer } from '@/types/customer-type';
import { Fulfillment } from '@/types/fulfillment-type';
import { OrderResponse } from '@/types/order-type';
import { Icon } from '@iconify/react';
import { Typography } from '@mui/material';
import { DropdownMenuItem } from '@radix-ui/react-dropdown-menu';
import { CreditCard, DollarSign, Hash, MapPin, Package, Phone, Settings, Truck } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { carrierLabel, formatPrice, isDeliveryMethod, methodLabel, SmallInfoTile } from './fulfillment-display';
import { UpdateDeliveryFeeDialog } from './UpdateDeliveryFeeDialog';

const SLATE = 'bg-slate-100 text-slate-600 border-slate-300';
const BLUE = 'bg-blue-100 text-blue-700 border-blue-300';
const EMERALD = 'bg-emerald-100 text-emerald-700 border-emerald-300';

const DELIVERY_STATUS: Record<string, { label: string; className: string }> = {
  NOT_PACKED: { label: 'Not packed', className: SLATE },
  PENDING: { label: 'Pending', className: SLATE },
  POSING: { label: 'Posing', className: 'bg-amber-100 text-amber-700 border-amber-300' },
  SHIPPING: { label: 'Shipping', className: BLUE },
  ARRIVAL: { label: 'Arrival', className: BLUE },
  DELIVERY: { label: 'Delivering', className: BLUE },
  DELIVERY_TO_CUSTOMER: { label: 'Delivery to customer', className: BLUE },
  READY_FOR_DELIVERY: {
    label: 'Ready for delivery',
    className: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  },
  RECEIVED: { label: 'Received', className: EMERALD },
  DELIVERED: { label: 'Delivered', className: EMERALD },
};

const MENU_ITEM_CLASS =
  'flex cursor-pointer items-center gap-3 rounded-md px-4 py-3 text-[14px] font-medium text-gray-700 transition-all hover:bg-indigo-50 hover:text-indigo-700';

function titleCase(value: string) {
  return value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function DeliveryStatusBadge({ status }: { status?: string | null }) {
  const badge = (status && DELIVERY_STATUS[status]) || {
    label: status ? titleCase(status) : 'Not packed',
    className: SLATE,
  };

  return (
    <Badge variant="secondary" className={cn('h-5 rounded-md px-2 text-[11px] font-medium', badge.className)}>
      {badge.label}
    </Badge>
  );
}

interface AddressCardProps {
  customer: Customer;
  order: OrderResponse;
  title: string;
  paymentMethod?: string | null;
  fulfillment?: Fulfillment | null;
  type: 'billing' | 'shipping';
  onReloadOrder?: () => void;
}

export default function AddressCard({
  customer,
  title,
  type,
  order,
  fulfillment,
  paymentMethod,
  onReloadOrder,
}: AddressCardProps) {
  const [isUpdateFeeDialogOpen, setIsUpdateFeeDialogOpen] = useState(false);
  const { createGoodRequestForFulfillment } = useCreateGoodRequestAction(onReloadOrder);

  if (!order || !customer) {
    return (
      <Card className="rounded-xl border border-slate-200 shadow-sm">
        <CardHeader className="px-4 py-3" />
      </Card>
    );
  }

  const hasAddress = Boolean(customer.address && customer.address.trim());

  return (
    <Card className="rounded-xl border border-slate-200 shadow-sm">
      <CardHeader className="px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-slate-600" />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                color: 'text.primary',
              }}
            >
              {title}
            </Typography>
          </div>
          <DeliveryStatusBadge status={order.deliveryStatus} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="float-right h-8 w-8 p-0 text-slate-600 hover:text-slate-900"
                title="Delivery Action"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="z-50 w-60 rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
            >
              <DropdownMenuItem
                disabled={!fulfillment?.id}
                onClick={() => {
                  if (!fulfillment?.id) return;
                  createGoodRequestForFulfillment(fulfillment.id);
                }}
                className={MENU_ITEM_CLASS}
              >
                <Icon icon="mdi:package-variant-closed-check" className="h-5 w-5 text-indigo-500" />
                Create Good Request
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setIsUpdateFeeDialogOpen(true)} className={MENU_ITEM_CLASS}>
                <Icon icon="mdi:currency-usd" className="h-5 w-5 text-indigo-500" />
                Update Delivery Fee
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent className="px-4 pb-4">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-sm font-medium text-slate-900">{customer.name || 'Unknown'}</p>
            {customer.phone && (
              <span className="inline-flex shrink-0 items-center gap-1 text-xs text-slate-600">
                <Phone className="h-3.5 w-3.5" />
                {customer.phone}
              </span>
            )}
          </div>

          <div className="mt-2 border-t border-slate-200 pt-2">
            {hasAddress ? (
              <div className="whitespace-pre-line text-sm leading-relaxed text-slate-800">{customer.address}</div>
            ) : (
              <p className="text-xs italic text-slate-500">No {type} address provided</p>
            )}
          </div>

          <div className="border-t p-1" />
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {fulfillment && (
              <SmallInfoTile
                icon={<Package className="h-3.5 w-3.5 text-slate-500" />}
                label="Shipping Method"
                value={methodLabel(fulfillment.method)}
              />
            )}
            {fulfillment && isDeliveryMethod(fulfillment.method) && fulfillment.carrier && (
              <SmallInfoTile
                icon={<Truck className="h-3.5 w-3.5 text-slate-500" />}
                label="Carrier"
                value={carrierLabel(fulfillment.carrier)}
              />
            )}
            <SmallInfoTile
              icon={<DollarSign className="h-3.5 w-3.5 text-slate-500" />}
              label="Delivery Fee"
              value={formatPrice(fulfillment?.price)}
            />
            <SmallInfoTile
              icon={<Hash className="h-3.5 w-3.5 text-slate-500" />}
              label="Tracking ID"
              value={fulfillment?.trackingNumber || 'Not set'}
              mono
            />
            <SmallInfoTile
              icon={<CreditCard className="h-3.5 w-3.5 text-slate-500" />}
              label="Payment Method"
              value={paymentMethod || 'Not specified'}
            />
          </div>
        </div>
      </CardContent>

      <UpdateDeliveryFeeDialog
        open={isUpdateFeeDialogOpen}
        onOpenChange={setIsUpdateFeeDialogOpen}
        fulfillment={fulfillment}
        onSuccess={onReloadOrder}
      />
    </Card>
  );
}
